import type { NavigateFunction } from 'react-router-dom';

interface SessionConfig {
  desktopTimeout: number; // milliseconds
  mobileTimeout: number;  // milliseconds
  warningBeforeLogout: number; // milliseconds
}

class SessionManager {
private config: SessionConfig = {
    desktopTimeout: 15 * 60 * 1000, // 15 minutes
    mobileTimeout: 10 * 60 * 1000,   // 10 minutes
    warningBeforeLogout: 60 * 1000,  // 1 minute warning
};

private activityTimer: ReturnType<typeof setTimeout> | null = null;
private warningTimer: ReturnType<typeof setTimeout> | null = null;
private navigate: NavigateFunction | null = null;
private onWarning: (() => void) | null = null;

  /**
   * Initialize session manager
   */
  private initialized = false;

  init(navigate: NavigateFunction, onWarning: (() => void) | null = null) {
    if (this.initialized) {
      this.cleanup(); // Prevent duplicates
    }
    this.initialized = true;
  
    this.navigate = navigate;
    this.onWarning = onWarning;
    this.startActivityTracking();
  }
  /**
   * Start tracking user activity
   */
  private startActivityTracking() {
    // Track these events as "activity"
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, this.resetTimer.bind(this), true);
    });

    // Handle visibility change (tab switching)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Start initial timer
    this.resetTimer();
  }

  /**
   * Reset the inactivity timer
   */
  private resetTimer() {
    this.clearTimers();
    
    const timeout = this.isMobile() 
      ? this.config.mobileTimeout 
      : this.config.desktopTimeout;

    // Set warning timer (1 minute before logout)
    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, timeout - this.config.warningBeforeLogout);

    // Set logout timer
    this.activityTimer = setTimeout(() => {
      this.logout('Session expired due to inactivity');
    }, timeout);
  }

  /**
   * Handle tab visibility change
   */
  private handleVisibilityChange() {
    if (document.hidden) {
      // User switched tabs or minimized - start countdown
      this.resetTimer();
    } else {
      // User came back - verify token is still valid
      this.verifySession();
    }
  }

  /**
   * Show warning modal before logout
   */
  private showWarning() {
    if (this.onWarning) {
      this.onWarning();
    }
  }

  /**
   * Verify if session is still valid
   */
  private verifySession() {
    const token = localStorage.getItem('token');
    if (!token) {
      this.logout('No active session found');
      return;
    }

    // Decode JWT to check expiration
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      
      if (Date.now() >= expiresAt) {
        this.logout('Token expired');
      }
    } catch (error) {
      this.logout('Invalid token');
    }
  }

   /**
    * Logout user
    */
   async logout(reason: string) {
     console.log(`Logging out: ${reason}`);
     
     // Clear all timers
     this.clearTimers();
     
     try {
       // Call backend logout endpoint if token exists
       const token = localStorage.getItem('token');
       if (token) {
         await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/logout`, {
           method: 'POST',
           headers: {
             'Authorization': `Bearer ${token}`,
             'Content-Type': 'application/json',
           },
         });
       }
     } catch (error) {
       console.error('Logout API error:', error);
       // Continue with local logout even if API fails
     }
     
     // Clear storage
     localStorage.removeItem('token');
     localStorage.removeItem('biometricCredentialId');
     sessionStorage.clear();
     
     // Navigate to auth
     if (this.navigate) {
       this.navigate('/auth', { 
         replace: true,
         state: { message: reason }
       });
     }
   }

  /**
   * Keep session alive (call on user activity)
   */
  keepAlive() {
    this.resetTimer();
  }

  /**
   * Clear all timers
   */
  private clearTimers() {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Detect if device is mobile
   */
  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth < 768;
  }

  /**
   * Cleanup on unmount
   */
  cleanup() {
    this.clearTimers();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.removeEventListener(event, this.resetTimer.bind(this), true);
    });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    this.initialized = false;
  }

  /**
   * Get remaining time until logout (in seconds)
   */
  getRemainingTime(): number {
    // This would need to track when the timer was started
    // For now, return timeout duration
    const timeout = this.isMobile() 
      ? this.config.mobileTimeout 
      : this.config.desktopTimeout;
    return Math.floor(timeout / 1000);
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();